import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button, Badge, Text, Table } from "@teals/ui-web";
import type { Column } from "@teals/ui-web";
import type { DataSource, SchemaField, FilterConfig } from "../../store/api/dataSourcesApi";
import {
  useUpdateAgentAssociationsMutation,
  useUpdateDataSourceMutation,
  useInsertDataSourceRecordsMutation,
  useVerifyConnectionMutation,
} from "../../store/api/dataSourcesApi";
import { useGetAgentsQuery } from "../../store/api/agentsApi";
import { useAuth } from "../../hooks/useAuth";
import { API_BASE_URL } from "../../utils/api";
import FilterConfigEditor from "./FilterConfigEditor";
import { emptyFilterConfig, toFilterConfigPayload } from "./filterConfig";
import { byoAppId, byoIndexName } from "./connectionConfig";
import { BYO_PROVIDER_LABELS } from "./constants";
import {
  AssociationItem,
  AssociationName,
  CodeBlock,
  CopyButton,
  DetailField,
  DetailFieldLabel,
  DetailFieldValue,
  DetailSection,
  DetailSectionTitle,
  KeyDisplay,
  OverviewLayout,
  AgentAddRow,
  SchemaHint,
  UploadZone,
  UploadZoneHint,
  UploadZoneIcon,
  UploadZoneText,
} from "./styles";

export type DataSourceDetailTab = "overview" | "upload";

interface DataSourceDetailProps {
  ds: DataSource;
  activeTab: DataSourceDetailTab;
}

const DataSourceDetail: React.FC<DataSourceDetailProps> = ({
  ds,
  activeTab,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [schemaFields, setSchemaFields] = useState<SchemaField[]>([]);
  const [schemaDirty, setSchemaDirty] = useState(false);
  const [filterConfig, setFilterConfig] =
    useState<FilterConfig>(emptyFilterConfig);
  const [filtersDirty, setFiltersDirty] = useState(false);
  const [addAgentId, setAddAgentId] = useState("");
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [verifyStatus, setVerifyStatus] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { user } = useAuth();
  const orgId = user?.organizationId || "";
  const { data: agents = [] } = useGetAgentsQuery(
    { orgId },
    { skip: !orgId },
  );
  const [updateAssociations] = useUpdateAgentAssociationsMutation();
  const [updateDataSource, { isLoading: savingDs }] =
    useUpdateDataSourceMutation();
  const [insertRecords, { isLoading: uploading }] =
    useInsertDataSourceRecordsMutation();
  const [verifyConnection, { isLoading: verifying }] =
    useVerifyConnectionMutation();

  useEffect(() => {
    setSelectedFile(null);
    setUploadStatus(null);
    setVerifyStatus(null);
    setSchemaDirty(false);
    setSchemaFields(ds.schemaDefinition?.fields ?? []);
    setFilterConfig(ds.filterConfig ?? emptyFilterConfig());
    setFiltersDirty(false);
    setAddAgentId("");
  }, [ds]);

  const isByo = ds.dsType === "byo";
  const providerLabel = ds.provider
    ? (BYO_PROVIDER_LABELS[ds.provider] ?? ds.provider)
    : null;

  const associatedAgents = useMemo(() => {
    const ids = new Set(ds.agentIds || []);
    return agents.filter((a) => ids.has(a.id) || ids.has(a.agentName));
  }, [ds.agentIds, agents]);

  const availableAgents = useMemo(() => {
    const ids = new Set(ds.agentIds || []);
    return agents.filter(
      (a) => !a.readOnly && !ids.has(a.id) && !ids.has(a.agentName),
    );
  }, [ds.agentIds, agents]);

  const recordsEndpoint = `${API_BASE_URL}/v1/data-sources/${ds.dataSourceId}/records`;
  const curlSnippet = `curl -X POST \\
  ${recordsEndpoint} \\
  -H "x-api-key: your-api-key" \\
  -H "x-secret-key: your-secret-key" \\
  -H "Content-Type: application/json" \\
  -d '{"title": "Example", "category": "demo"}'`;

  const schemaColumns: Column<SchemaField>[] = useMemo(
    () => [
      { key: "name", header: "Field Name" },
      { key: "type", header: "Type" },
      {
        key: "searchable",
        header: "Searchable",
        align: "center",
        render: (_val: boolean, row: SchemaField) => (
          <input
            type="checkbox"
            aria-label={`${row.name} searchable`}
            checked={row.searchable}
            onChange={() => {
              setSchemaFields((prev) =>
                prev.map((f) =>
                  f.name === row.name
                    ? { ...f, searchable: !f.searchable }
                    : f,
                ),
              );
              setSchemaDirty(true);
            }}
          />
        ),
      },
      {
        key: "indexable",
        header: "Indexable",
        align: "center",
        render: (_val: boolean, row: SchemaField) => (
          <input
            type="checkbox"
            aria-label={`${row.name} indexable`}
            checked={row.indexable}
            onChange={() => {
              setSchemaFields((prev) =>
                prev.map((f) =>
                  f.name === row.name ? { ...f, indexable: !f.indexable } : f,
                ),
              );
              setSchemaDirty(true);
            }}
          />
        ),
      },
    ],
    [],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setUploadStatus(null);
  };

  const handleZoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleSaveSchema = async () => {
    if (
      !window.confirm(
        "Saving schema changes may affect agents that query this data source. Continue?",
      )
    ) {
      return;
    }
    await updateDataSource({
      id: ds.dataSourceId,
      body: { schema_definition: { fields: schemaFields } },
    }).unwrap();
    setSchemaDirty(false);
  };

  const handleSaveFilters = async () => {
    await updateDataSource({
      id: ds.dataSourceId,
      body: { filter_config: toFilterConfigPayload(filterConfig) ?? null },
    }).unwrap();
    setFiltersDirty(false);
  };

  const handleAddAgent = async () => {
    if (!addAgentId) return;
    const next = [...(ds.agentIds || []), addAgentId];
    await updateAssociations({ id: ds.dataSourceId, agentIds: next }).unwrap();
    setAddAgentId("");
  };

  const handleRemoveAgent = async (agentId: string) => {
    const next = (ds.agentIds || []).filter((id) => id !== agentId);
    await updateAssociations({ id: ds.dataSourceId, agentIds: next }).unwrap();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploadStatus(null);
    try {
      const text = await selectedFile.text();
      let records: Record<string, unknown>[];
      if (selectedFile.name.endsWith(".csv")) {
        const lines = text.trim().split(/\r?\n/);
        if (lines.length < 2) {
          setUploadStatus("CSV must include a header row and at least one record.");
          return;
        }
        const headers = lines[0].split(",").map((h) => h.trim());
        records = lines.slice(1).map((line) => {
          const cols = line.split(",");
          const row: Record<string, unknown> = {};
          headers.forEach((h, i) => {
            row[h] = (cols[i] || "").trim();
          });
          return row;
        });
      } else {
        const parsed = JSON.parse(text);
        records = Array.isArray(parsed) ? parsed : [parsed];
      }
      const result = await insertRecords({
        id: ds.dataSourceId,
        records,
      }).unwrap();
      setUploadStatus(
        `Uploaded ${result.insertedCount} records (total ${result.recordCount}).`,
      );
      setSelectedFile(null);
    } catch (err) {
      setUploadStatus(
        err instanceof Error ? err.message : "Upload failed. Try again.",
      );
    }
  };

  const handleVerify = async () => {
    setVerifyStatus(null);
    try {
      const result = await verifyConnection(ds.dataSourceId).unwrap();
      const sampleCount = result.sampleRecords?.length ?? 0;
      setVerifyStatus(
        result.verified
          ? `Verified. ${sampleCount} sample record${sampleCount === 1 ? "" : "s"} loaded.`
          : "Verification did not succeed.",
      );
    } catch (err) {
      setVerifyStatus(
        err instanceof Error ? err.message : "Verification failed.",
      );
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const indexName = byoIndexName(ds.connectionConfig);
  const appId = byoAppId(ds.connectionConfig);

  if (activeTab === "upload" && !isByo) {
    return (
      <>
        <Text
          variant="body1"
          style={{ marginBottom: 16, color: "var(--text-secondary)" }}
        >
          Manually upload records to <strong>{ds.name}</strong>. For
          production ingestion, use the Direct API with your API Key
          Profile credentials.
        </Text>

        <UploadZone onClick={handleZoneClick}>
          <UploadZoneIcon>{"\uD83D\uDCC2"}</UploadZoneIcon>
          <UploadZoneText>
            Drop JSON or CSV files here, or click to browse
          </UploadZoneText>
          <UploadZoneHint>Max 10 MB per file</UploadZoneHint>
        </UploadZone>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json,.csv"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {selectedFile && (
          <div style={{ marginTop: 12 }}>
            <Text
              variant="body1"
              style={{
                fontSize: "0.875rem",
                color: "var(--text-primary)",
              }}
            >
              {selectedFile.name} (
              {(selectedFile.size / 1024).toFixed(1)} KB)
            </Text>
            <Button
              variant="primary"
              size="sm"
              style={{ marginTop: 8 }}
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </div>
        )}

        {uploadStatus && (
          <Text
            variant="body1"
            style={{
              marginTop: 12,
              fontSize: "0.875rem",
              color: "var(--text-secondary)",
            }}
          >
            {uploadStatus}
          </Text>
        )}
      </>
    );
  }

  return (
    <OverviewLayout>
      <div>
        <DetailSection>
          <DetailSectionTitle>Schema</DetailSectionTitle>
          {schemaFields.length ? (
            <>
              <Table
                columns={schemaColumns}
                data={schemaFields}
                rowKey="name"
              />
              <SchemaHint>
                Schema changes require confirmation — they may affect running
                agents.
              </SchemaHint>
              {schemaDirty && (
                <Button
                  variant="primary"
                  size="sm"
                  style={{ marginTop: 12 }}
                  onClick={handleSaveSchema}
                  disabled={savingDs}
                >
                  {savingDs ? "Saving…" : "Save schema"}
                </Button>
              )}
            </>
          ) : (
            <Text
              variant="body1"
              style={{
                color: "var(--text-muted)",
                fontSize: "0.8125rem",
              }}
            >
              No schema fields defined
            </Text>
          )}
        </DetailSection>

        <DetailSection>
          <DetailSectionTitle>Query filters</DetailSectionTitle>
          <FilterConfigEditor
            value={filterConfig}
            onChange={(next) => {
              setFilterConfig(next);
              setFiltersDirty(true);
            }}
            fieldOptions={schemaFields.map((f) => f.name)}
          />
          {filtersDirty && (
            <Button
              variant="primary"
              size="sm"
              style={{ marginTop: 12 }}
              onClick={handleSaveFilters}
              disabled={savingDs}
            >
              {savingDs ? "Saving…" : "Save filters"}
            </Button>
          )}
        </DetailSection>

        {isByo ? (
          <DetailSection>
            <DetailSectionTitle>Connection</DetailSectionTitle>
            <Text
              variant="body1"
              style={{
                marginBottom: 12,
                color: "var(--text-muted)",
                fontSize: "0.8125rem",
              }}
            >
              Records stay in the linked {providerLabel || "external"} index.
              Never paste provider keys into Agent Builder chat.
            </Text>
            <DetailField>
              <DetailFieldLabel>Provider</DetailFieldLabel>
              <DetailFieldValue>
                <Badge variant="neutral" size="sm">
                  {providerLabel || ds.provider || "BYO"}
                </Badge>
              </DetailFieldValue>
            </DetailField>
            {appId && (
              <DetailField>
                <DetailFieldLabel>Application ID</DetailFieldLabel>
                <DetailFieldValue style={{ fontFamily: "monospace" }}>
                  {appId}
                </DetailFieldValue>
              </DetailField>
            )}
            {indexName && (
              <DetailField>
                <DetailFieldLabel>Index</DetailFieldLabel>
                <DetailFieldValue style={{ fontFamily: "monospace" }}>
                  {indexName}
                  <CopyButton
                    onClick={() => copyToClipboard(indexName)}
                    style={{ marginLeft: 8 }}
                  >
                    Copy
                  </CopyButton>
                </DetailFieldValue>
              </DetailField>
            )}
            <Button
              variant="outline"
              size="sm"
              style={{ marginTop: 12 }}
              onClick={handleVerify}
              disabled={verifying}
            >
              {verifying ? "Verifying…" : "Verify connection"}
            </Button>
            {verifyStatus && (
              <Text
                variant="body1"
                style={{
                  marginTop: 8,
                  fontSize: "0.8125rem",
                  color: "var(--text-secondary)",
                }}
              >
                {verifyStatus}
              </Text>
            )}
          </DetailSection>
        ) : (
          <DetailSection>
            <DetailSectionTitle>Connection / Direct API</DetailSectionTitle>
            <Text
              variant="body1"
              style={{
                marginBottom: 12,
                color: "var(--text-muted)",
                fontSize: "0.8125rem",
              }}
            >
              Insert records with API Key Profile headers. Never paste secrets
              into Agent Builder chat.
            </Text>
            <KeyDisplay>
              {recordsEndpoint}
              <CopyButton onClick={() => copyToClipboard(recordsEndpoint)}>
                Copy
              </CopyButton>
            </KeyDisplay>
            <CodeBlock style={{ marginTop: 12 }}>{curlSnippet}</CodeBlock>
          </DetailSection>
        )}
      </div>

      <div>
        <DetailSection>
          <DetailSectionTitle>Agent associations</DetailSectionTitle>
          {associatedAgents.length > 0 ? (
            associatedAgents.map((agent) => (
              <AssociationItem key={agent.id}>
                <AssociationName>
                  <Link
                    to={`/agents/${encodeURIComponent(agent.id)}?tab=configure`}
                    style={{ color: "inherit" }}
                  >
                    {agent.name}
                  </Link>
                </AssociationName>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveAgent(agent.id)}
                >
                  Remove
                </Button>
              </AssociationItem>
            ))
          ) : (
            <Text
              variant="body1"
              style={{
                color: "var(--text-muted)",
                fontSize: "0.8125rem",
              }}
            >
              No agents associated
            </Text>
          )}
          <AgentAddRow>
            <select
              value={addAgentId}
              onChange={(e) => setAddAgentId(e.target.value)}
              aria-label="Select agent…"
            >
              <option value="">Select agent…</option>
              {availableAgents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleAddAgent}
              disabled={!addAgentId}
            >
              + Add Agent
            </Button>
          </AgentAddRow>
        </DetailSection>
      </div>
    </OverviewLayout>
  );
};

export default DataSourceDetail;
